import { ACTION_ITEM_STATUS, ActionItemStatusSchema } from '@pique/core/modules/properties/services/schema';

// Centralized status colors - used across components
// These are solid colors for icons, strokes, and status indicators
// For translucent backgrounds, use CSS variables: var(--pique-green-gradient-light), etc.
export const STATUS_COLORS = {
  TODO: '#9B5DE5',
  IN_PROGRESS: '#F5A623',
  DONE: 'var(--pique-green-primary)',
  DONE_HEX: '#2ECC71',
  DONE_DARK: 'var(--pique-green-dark)',
} as const;

interface StatusConfig {
  color: string;
  defaultProgress: number;
  minVisualProgress: number;
  displayName: string;
  keywords: string[];
}

export const STATUS_CONFIGS: Record<string, StatusConfig> = {
  'to-do': {
    color: STATUS_COLORS.TODO,
    defaultProgress: 0,
    minVisualProgress: 5,
    displayName: ACTION_ITEM_STATUS.TODO,
    keywords: ['to do', 'todo', 'not started', '']
  },
  'in-progress': {
    color: STATUS_COLORS.IN_PROGRESS,
    defaultProgress: 50,
    minVisualProgress: 50,
    displayName: ACTION_ITEM_STATUS.IN_PROGRESS,
    keywords: ['in progress', 'progress', 'doing', 'started']
  },
  'done': {
    color: STATUS_COLORS.DONE,
    defaultProgress: 100,
    minVisualProgress: 100,
    displayName: ACTION_ITEM_STATUS.DONE,
    keywords: ['done', 'complete', 'completed', 'finished']
  }
};

export const STATUS_OPTIONS = ActionItemStatusSchema.options;

export function getStatusConfig(status: string | null | undefined): StatusConfig {
  const normalizedStatus = (status || '').toLowerCase().trim();

  // Find matching config by keywords
  for (const [_key, config] of Object.entries(STATUS_CONFIGS)) {
    if (config.keywords.some(keyword =>
      keyword === normalizedStatus ||
      (keyword && normalizedStatus.includes(keyword))
    )) {
      return config;
    }
  }

  // Default to 'to-do' if no match
  const defaultConfig = STATUS_CONFIGS['to-do'];
  if (!defaultConfig) {
    throw new Error('Missing default status config for to-do');
  }
  return defaultConfig;
}

export function getVisualProgress(
  status: string | null | undefined,
  actualProgress: number,
  isVisuallyComplete: boolean = false
): number {
  if (isVisuallyComplete || actualProgress >= 100) return 100;

  const config = getStatusConfig(status);
  const baseProgress = actualProgress > 0 ? actualProgress : config.defaultProgress;

  // Ensure minimum visual progress for better UX
  return Math.max(baseProgress, config.minVisualProgress);
}

export function getStatusColor(status: string | null | undefined): string {
  return getStatusConfig(status).color;
}
